import React, { memo, useCallback, useState } from 'react'
import { makeStyles, useTheme } from '@material-ui/styles'
import classNames from 'classnames'
import { Grid, Theme } from '@material-ui/core'
import Hidden from '@material-ui/core/Hidden'
import Button from '@material-ui/core/Button'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery'
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer'
import InfiniteScroll from 'react-infinite-scroller'
import { useLocation, matchPath, useParams } from 'react-router-dom'
import ClassNames from 'classnames'

import { JobsFeed, Loading, JobsFilterSection } from 'components'
import generalStyles from '../../styles'
import styles from './styles'
import { StoreValue } from 'store'
import { ConditionalWrapper, combineStyles } from 'utils'
import { SearchEmptyResult } from '..'
import { JobApplicants } from 'screens/search/components'
import { useFeedJobsQuery } from 'graphql/__generated__/graphql'

const combinedStyles: any = combineStyles(generalStyles, styles)
const useStyles = makeStyles(combinedStyles)

const JobsView: React.FunctionComponent<any> = ({
	showJobs = true,
	storeSearchDebounceQuery,
	storeSearchLoading,
	breadcrumbPath,
}) => {
	const { companySlug }: { companySlug?: string } = useParams()
	const location = useLocation()
	const isCompanySettingsPage = matchPath(location.pathname, {
		path: '/:language(en|ro)/employers/:companySlug/company-settings',
		exact: false,
	})
	const { state }: { state?: { companyName?: string; teamName?: string } } =
		useLocation()

	const { t } = useTranslation()
	const theme: Theme = useTheme()
	const isMdDown: boolean = useMediaQuery(theme.breakpoints.down('md'))

	const classes = useStyles()
	const [isFilterVisible, setIsFilterVisible] = React.useState(false)
	const [title, setTitle] = useState<string | null>(null)
	const [companyName, setCompanyName] = React.useState(
		state && state.companyName ? state.companyName : null,
	)
	const [teamName, setTeamName] = React.useState(
		state && state.teamName ? state.teamName : null,
	)
	const [locationsIds, setLocationsIds] = useState<string[] | null>([])
	const [skillsIds, setSkillsIds] = useState<string[] | null>([])
	const [salaryMin, setSalaryMin] = useState<string>('0')
	const [salaryMax, setSalaryMax] = useState<string>('0')
	const [currency, setCurrency] = useState<string | null>(null)
	const [jobTypes, setJobTypes] = useState<string | null>(null)
	const [seniorityLevels, setSeniorityLevels] = useState<string | null>(null)
	const [contractTypes, setContractTypes] = useState<string | null>(null)
	const [locationTypes, setLocationTypes] = useState<string | null>(null)
	const [departmentIds, setDepartmentIds] = useState(undefined)
	const {
		data: feedJobsData,
		loading: feedJobsLoading,
		fetchMore: fetchFeedJobs,
	} = useFeedJobsQuery({
		fetchPolicy: 'network-only',
		variables: {
			name: isCompanySettingsPage?.isExact
				? title
				: storeSearchDebounceQuery,
			departmentids: departmentIds,
			companyname: companyName,
			companyslug: companySlug,
			teamname: teamName,
			locationsids: locationsIds,
			skills: skillsIds,
			salarymin: String(parseFloat(salaryMin)),
			salarymax: String(parseFloat(salaryMax)),
			typeofjob: jobTypes ? jobTypes : [],
			currency: currency ? currency : null,
			senioritylevels: seniorityLevels ? seniorityLevels : [],
			contracttypes: contractTypes ? contractTypes : [],
			locationtypes: locationTypes ? locationTypes : [],
			first: 5,
			after: null,
		},
	})
	const loading = storeSearchLoading || feedJobsLoading
	const jobs: any = feedJobsData
		? feedJobsData &&
		  feedJobsData.feedjobs &&
		  feedJobsData.feedjobs?.edges?.map((edge: any) => edge.node)
		: []
	// console.log({ jobs, feedJobsData });

	const hasNextPage = feedJobsData
		? feedJobsData &&
		  feedJobsData.feedjobs &&
		  feedJobsData.feedjobs.pageInfo.hasNextPage
		: false

	const handleSetTitle = useCallback((title: string) => {
		setTitle(title)
	}, [])

	const handleSetLocationsIds = useCallback(
		(locationsIds: string[]) => {
			setLocationsIds(locationsIds)
		},
		[setLocationsIds],
	)
	const handleSetSkillsIds = useCallback((skillsIds: string[]) => {
		setSkillsIds(skillsIds)
	}, [])

	const handleSetMaxSalary = useCallback(value => {
		setSalaryMax(value)
	}, [])
	const handleSetMinSalry = useCallback(value => {
		setSalaryMin(value)
	}, [])
	const handleSetJobTypes = useCallback(value => {
		setJobTypes(value)
	}, [])

	const handleSetCurrency = useCallback(value => {
		setCurrency(value)
	}, [])

	const handleSetSeniorityLevels = useCallback(value => {
		setSeniorityLevels(value)
	}, [])

	const handleSetContractTypes = useCallback(value => {
		setContractTypes(value)
	}, [])

	const handleSetLocationTypes = useCallback(value => {
		setLocationTypes(value)
	}, [])

	const handleSetDepartmentIds = useCallback(ids => {
		if (ids && ids.length > 0) {
			setDepartmentIds(ids)
		} else {
			setDepartmentIds(undefined)
		}
	}, [])
	// const handleSetCompanyId = useCallback(id => {
	// 	if (id) {
	// 		setCompanyId(id)
	// 	} else {
	// 		setCompanyId('')
	// 	}
	// }, [])

	const showFilter = useCallback(() => {
		setIsFilterVisible(true)
	}, [])
	const closeFilter = useCallback(() => {
		setIsFilterVisible(false)
	}, [])

	const toggleDrawer = useCallback(
		(open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
			if (
				event &&
				event.type === 'keydown' &&
				((event as React.KeyboardEvent).key === 'Tab' ||
					(event as React.KeyboardEvent).key === 'Shift')
			) {
				return
			}

			setIsFilterVisible(open)
		},
		[],
	)
	const handleSetCompanyName = useCallback(value => {
		setCompanyName(value)
	}, [])
	const handleSetTeamName = useCallback(value => {
		setTeamName(value)
	}, [])

	return (
		<>
			<Grid
				container
				justifyContent="center"
				style={{ marginTop: 35 }}
				className={ClassNames(classes.container, {
					[classes.relativeContainer]: isCompanySettingsPage?.isExact,
				})}
			>
				{!breadcrumbPath && (
					<>
						<ConditionalWrapper
							condition={showJobs}
							wrapper={(children: any) => (
								<Grid
									className={classes.jobsContainer}
									direction="column"
								>
									{!loading && (
										<Grid
											className={classNames(
												classes.jobsMainContent,
												{
													[classes.subHeaderContent]:
														isCompanySettingsPage,
												},
											)}
											container={true}
											direction="column"
										>
											<div
												className={classNames(
													classes.pageTitle,
													{
														[classes.titleWithFilterButton]:
															!isCompanySettingsPage,
														[classes.onlyFilterButton]:
															isCompanySettingsPage,
													},
												)}
											>
												{!isCompanySettingsPage && (
													<div>
														{t(`Explore`)}&nbsp;
														<span
															className={
																classes.pageTitleMain
															}
														>
															{t(`Jobs`)}
														</span>
													</div>
												)}
												<div>
													<Hidden only={['lg', 'xl']}>
														<Button
															classes={{
																root: classes.filterSectionButton,
															}}
															variant="contained"
															color="primary"
															onClick={toggleDrawer(
																true,
															)}
														>
															{t('Filter')}
														</Button>
													</Hidden>
												</div>
											</div>
										</Grid>
									)}
									{children}
								</Grid>
							)}
						>
							{loading && (
								<Loading
									size={48}
									style={{ margin: '50px auto' }}
								/>
							)}
							{!loading && jobs.length === 0 && (
								<SearchEmptyResult
									query={storeSearchDebounceQuery}
								/>
							)}
							{!loading && jobs.length > 0 && (
								<div
									className={classNames(
										classes.tabsContentContainer,
										classes.tabsContentContainerCompany,
										classes.fixFlexList,
									)}
								>
									<InfiniteScroll
										pageStart={0}
										loadMore={() =>
											fetchFeedJobs({
												variables: {
													data: {
														name: storeSearchDebounceQuery,
														departmentIds,
														locationsIds,
														companyName,
														first: 5,
														after: feedJobsData
															?.feedjobs?.edges[
															feedJobsData
																?.feedjobs
																?.edges
																?.length - 1
														].cursor,
													},
												},
												updateQuery: (
													previousQueryResult,
													{ fetchMoreResult },
												) => {
													if (
														!fetchMoreResult ||
														!fetchMoreResult.feedjobs
													) {
														return previousQueryResult
													}

													const {
														edges: newEdges,
														pageInfo,
													} = fetchMoreResult.feedjobs

													return newEdges.length > 0
														? {
																feedjobs: {
																	__typename:
																		previousQueryResult
																			.feedjobs
																			?.__typename,
																	edges: [
																		...(previousQueryResult
																			?.feedjobs
																			?.edges ||
																			[]),
																		...newEdges,
																	],
																	pageInfo,
																},
														  }
														: previousQueryResult
												},
											})
										}
										hasMore={hasNextPage ?? undefined}
										loader={
											<div
												style={{
													flexGrow: 1,
													display: 'flex',
												}}
												key={'loading'}
											>
												<Loading
													size={48}
													style={{
														margin: '50px auto',
													}}
												/>
											</div>
										}
										className={classes.searchContentCards}
									>
										{jobs && <JobsFeed jobsData={jobs} />}
									</InfiniteScroll>
								</div>
							)}
						</ConditionalWrapper>

						{!isCompanySettingsPage ? (
							<>
								<Hidden mdDown>
									<div
										className={classNames(
											classes.filterSection,
											{
												[classes.filterSectionWithSmallerTop]:
													isCompanySettingsPage,
											},
										)}
									>
										<JobsFilterSection
											handleTitle={handleSetTitle}
											handleDepartmentIds={
												handleSetDepartmentIds
											}
											handleLocationsIds={
												handleSetLocationsIds
											}
											handleCompanyName={
												handleSetCompanyName
											}
											handleTeamName={handleSetTeamName}
											handleSkills={handleSetSkillsIds}
											handleMinSalary={handleSetMinSalry}
											handleMaxSalary={handleSetMaxSalary}
											handleCurrency={handleSetCurrency}
											handleJobTypes={handleSetJobTypes}
											handleSeniorityLevels={
												handleSetSeniorityLevels
											}
											handleContractTypes={
												handleSetContractTypes
											}
											handleLocationTypes={
												handleSetLocationTypes
											}
										/>
									</div>
								</Hidden>
								{isMdDown && (
									<>
										<SwipeableDrawer
											anchor="right"
											open={isFilterVisible}
											onOpen={showFilter}
											onClose={closeFilter}
											classes={{
												paper: classes.filterDrawer,
											}}
										>
											<div
												className={
													classes.jobsSearchContainer
												}
											>
												<JobsFilterSection
													handleTitle={handleSetTitle}
													handleDepartmentIds={
														handleSetDepartmentIds
													}
													handleLocationsIds={
														handleSetLocationsIds
													}
													handleTeamName={
														handleSetTeamName
													}
													handleCompanyName={
														handleSetCompanyName
													}
													handleSkills={
														handleSetSkillsIds
													}
													handleMinSalary={
														handleSetMinSalry
													}
													handleMaxSalary={
														handleSetMaxSalary
													}
													handleCurrency={
														handleSetCurrency
													}
													handleJobTypes={
														handleSetJobTypes
													}
													handleSeniorityLevels={
														handleSetSeniorityLevels
													}
													handleContractTypes={
														handleSetContractTypes
													}
													handleLocationTypes={
														handleSetLocationTypes
													}
													toggleDrawer={toggleDrawer}
												/>
											</div>
										</SwipeableDrawer>
									</>
								)}
							</>
						) : (
							<SwipeableDrawer
								anchor="right"
								open={isFilterVisible}
								onOpen={showFilter}
								onClose={closeFilter}
								classes={{
									paper: classes.filterDrawer,
								}}
							>
								<div className={classes.jobsSearchContainer}>
									<JobsFilterSection
										handleTitle={handleSetTitle}
										handleDepartmentIds={
											handleSetDepartmentIds
										}
										handleLocationsIds={
											handleSetLocationsIds
										}
										handleTeamName={handleSetTeamName}
										handleCompanyName={handleSetCompanyName}
										handleSkills={handleSetSkillsIds}
										handleMinSalary={handleSetMinSalry}
										handleMaxSalary={handleSetMaxSalary}
										handleCurrency={handleSetCurrency}
										handleJobTypes={handleSetJobTypes}
										handleSeniorityLevels={
											handleSetSeniorityLevels
										}
										handleContractTypes={
											handleSetContractTypes
										}
										handleLocationTypes={
											handleSetLocationTypes
										}
										toggleDrawer={toggleDrawer}
									/>
								</div>
							</SwipeableDrawer>
						)}
					</>
				)}
			</Grid>
			<Grid className={classes.btnSection}>
				{/* See Applicants */}
				{breadcrumbPath && (
					<div>
						<JobApplicants />
					</div>
				)}
			</Grid>
		</>
	)
}

const Jobs = memo((props: any) => (
	<StoreValue
		keys={[
			'storeDispatch',
			'search.debounceQuery|storeSearchDebounceQuery',
			'search.loading|storeSearchLoading',
			'company.breadcrumbPath.path|breadcrumbPath',
		]}
	>
		<JobsView {...props} />
	</StoreValue>
))

export default Jobs
